<template>
    <div>
        <header>
            <v-container>
                <v-row no-gutters>
                    <h1 class="font-weight-light headline">
                        {{ $t("title.translation") }}
                    </h1>
                    <v-spacer></v-spacer>
                    <v-lazy v-if="isTranslationsLoaded" transition="fade-transition">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            :label="searchLabel"
                            single-line
                            dense
                            hide-details
                            style="max-width: 200px"
                            class="mr-5 pr-5"
                        ></v-text-field>
                    </v-lazy>
                    <v-lazy transition="fade-transition">
                        <v-btn
                            color="success"
                            dark
                            small
                            @click.stop="
                                dialog.translationForm = true;
                                isTranslationEdited = false;
                            "
                            >{{ $t("general.add") }}</v-btn
                        >
                    </v-lazy>
                </v-row>
            </v-container>
        </header>
        <section class="py-7">
            <v-container>
                <v-sheet class="px-3 pt-3 pb-3" v-if="!isTranslationsLoaded">
                    <v-skeleton-loader
                        class="mx-auto"
                        type="table"
                    ></v-skeleton-loader>
                </v-sheet>
                <v-card v-else>
                    <v-data-table
                        :headers="headers"
                        :items="translations"
                        :search="search"
                    >
                        <template v-slot:item.action="{ item }">
                            <div style="display: flex">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            v-tr-class-on-hover="'blue'"
                                            size="20px"
                                            color="blue"
                                            class="mr-2"
                                            @click="editTranslation(item)"
                                            v-on="on"
                                            >mdi-pencil-outline</v-icon
                                        >
                                    </template>
                                    <span>{{ $t("general.edit") }}</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            size="22px"
                                            color="red"
                                            v-tr-class-on-hover="'red'"
                                            class="mr-2"
                                            @click="confirmDelete(item)"
                                            v-on="on"
                                            >mdi-delete-outline</v-icon
                                        >
                                    </template>
                                    <span>{{ $t("general.delete") }}</span>
                                </v-tooltip>
                            </div>
                        </template>
                        <template v-slot:item.key="{ item }">
                            {{ item.key }}
                        </template>
                        <template v-slot:item.value="{ item }">
                            {{ item.value }}
                        </template>
                        <!-- dynamic language columns -->
                        <template
                            v-for="language in languages"
                            v-slot:[`item.value_${language.code}`]="{ item }"
                        >
                            <div :key="`lang-${language.code}`">
                                {{ item[`value_${language.code}`] || '-' }}
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
                <TranslationForm
                    :translation="selectedTranslation"
                    :languages="languages"
                    :visible="dialog.translationForm"
                    :isEdited="isTranslationEdited"
                    @addTranslation="addTranslation"
                    @updateTranslation="updateTranslation"
                    @close="dialog.translationForm = false"
                    @snackMessage="snackMessage"
                />

                <ConfirmationDialog
                    :name="selectedTranslation.key"
                    :text="'translations.delete_confirmation'"
                    v-if="dialog.confirmation"
                    @confirm="deleteTranslation"
                    @cancel="cancelDelete"
                />

                <SnackMessage ref="SnackMessage" />
            </v-container>
        </section>
    </div>
</template>

<script>
import TranslationForm from "../components/TranslationForm";
import ConfirmationDialog from "../components/ConfirmationDialog";
import SnackMessage from "../components/SnackMessage";

export default {
    data() {
        return {
            languages: [],
            translations: [],
            isTranslationsLoaded: false,
            search: "",
            dialog: {
                confirmation: false,
                translationForm: false,
            },
            selectedTranslation: {
                key: "",
                value: ""
                // Los campos value_[code] se agregarán dinámicamente
            },
            defaultTranslation: {
                key: "",
                value: ""
                // Los campos value_[code] se agregarán dinámicamente
            },
            isTranslationEdited: false,
        };
    },

    components: {
        TranslationForm,
        ConfirmationDialog,
        SnackMessage,
    },

    computed: {
        headers() {
            const baseHeaders = [
                {
                    text: this.$t("general.actions"),
                    value: "action",
                    sortable: false,
                    width: "100px"
                },
                {
                    text: this.$t("translations.section_key"),
                    align: "left",
                    sortable: true,
                    value: "key",
                },
                {
                    text: this.$t("translations.default_value"),
                    align: "left",
                    sortable: true,
                    value: "value",
                }
            ];
            
            // Añadir headers dinámicos para cada idioma
            const languageHeaders = this.languages.filter(lang => lang.code !== "cz")  
            .map(lang => ({
            text: lang.name + " (" + lang.code + ")",
            align: "left",
            sortable: true,
            value: `value_${lang.code}`,
            }));
            
            return [...baseHeaders, ...languageHeaders];
        },
        searchLabel() {
            return this.$t("general.search");
        }, 
    },

    created() {
        this.getTranslations();
    },
    
    watch: {
        languages(newLanguages) {
            // Actualizar el objeto defaultTranslation cuando cambian los idiomas
            const defTranslation = {
                key: "",
                value: ""
            };
            
            newLanguages.forEach(lang => {
                defTranslation[`value_${lang.code}`] = '';
            });
            
            this.defaultTranslation = defTranslation;
            
            // Actualizar también selectedTranslation si está en su estado por defecto
            if (!this.selectedTranslation.id) {
                this.selectedTranslation = { ...defTranslation };
            }
        }
    },

    methods: {
        snackMessage(msg, type) {
            this.$refs.SnackMessage.showMessage(msg, type);
        },

        async getTranslations() {
            try {
                const response = await axios.get("/translations", {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                });
                
                this.languages = response.data.languages.filter(lang => lang.code !== "cz");
                this.translations = response.data.translations;
                this.isTranslationsLoaded = true;
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        },
        
        addTranslation(translation) {
            this.translations.push(translation);
        },
        
        editTranslation(item) {
            this.selectedTranslation = Object.assign({}, item);
            this.dialog.translationForm = true;
            this.isTranslationEdited = true;
        },
        
        updateTranslation(translation) {
            let index = this.translations.findIndex((item) => item.id == translation.id);
            this.translations.splice(index, 1, translation);
        },
        
        confirmDelete(item) {
            this.selectedTranslation = item;
            this.dialog.confirmation = true;
        },
        
        cancelDelete() {
            this.dialog.confirmation = false;
        },
        
        async deleteTranslation() {
            this.dialog.confirmation = false;
            let index = this.translations.indexOf(this.selectedTranslation);

            try {
                await axios.delete(
                    `/translations/${this.selectedTranslation.id}`,
                    {
                        headers: {
                            Authorization:
                                "Bearer " +
                                this.$store.state.tokenData.user.access_token,
                        },
                    }
                );
                
                this.translations.splice(index, 1);
                this.selectedTranslation = { ...this.defaultTranslation };
                this.snackMessage("translations.deleted_successfully", "success");
            } catch (error) {
                console.error(error.response?.data || error);
                this.snackMessage("general.error", "error");
            }
        }
    },
};
</script>